import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Award, Eye } from "lucide-react";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Reactable } from "../../components/ui/reactable";
import { DialogModal } from "../../components/ui/dialogModal";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { getPrintData } from "../../components/common/CommonFunctions";
import { MY_INTERNSHIP, INTERNSHIP_CERTIFICATE } from "../../constants/api";
import { EnrolmentStatusPill, PhaseStatusPill, enrolmentStatusLabels } from "./internshipUi";

const STATUS_TABS = [
    { value: "", label: "All" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "withdrawn", label: "Withdrawn" },
];

const MyInternship = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const [enrolments, setEnrolments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [detail, setDetail] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const filters = statusFilter ? { status: statusFilter } : {};
            const res = await fetch(MY_INTERNSHIP, filters);
            setEnrolments(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load your internship");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => { loadData(); }, [loadData]);

    const columns = [
        {
            accessor: "program_name", Header: "Program", sortable: true, filterable: true,
            Cell: ({ value }) => <span className="font-medium text-foreground">{value || "-"}</span>,
        },
        {
            accessor: "mentor_name", Header: "Mentor", sortable: true, filterable: true,
            Cell: ({ value }) => value || "Not assigned",
        },
        {
            accessor: "start_date", Header: "Start Date", sortable: true, filterable: true, filterType: "date",
            Cell: ({ value }) => (value ? dayjs(value).format("DD-MM-YYYY") : "-"),
        },
        {
            accessor: "expected_end_date", Header: "Expected End", sortable: true, filterable: true, filterType: "date",
            Cell: ({ value }) => (value ? dayjs(value).format("DD-MM-YYYY") : "-"),
        },
        {
            accessor: "current_phase", Header: "Current Phase", sortable: true, filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "progress_percent", Header: "Progress", sortable: true,
            Cell: ({ row }) => {
                const phases = row.phase_progress || [];
                const done = phases.filter((p) => p.status === "completed" || p.status === "evaluated").length;
                const pct = row.progress_percent ?? 0;
                return (
                    <div className="min-w-[110px]">
                        <div className="flex items-center justify-between text-xs mb-1">
                            <span>{pct}%</span>
                            {phases.length ? (
                                <span className="text-muted-foreground">{done}/{phases.length}</span>
                            ) : null}
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-sidebar-primary"
                                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            accessor: "status", Header: "Status", sortable: true, filterable: true,
            filterType: "select",
            filterOptions: Object.entries(enrolmentStatusLabels).map(([value, label]) => ({ value, label })),
            Cell: ({ value }) => <EnrolmentStatusPill value={value} />,
        },
        {
            accessor: "actions", Header: "Actions",
            Cell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <Button
                        size="sm"
                        variant="ghost"
                        title="View phases"
                        onClick={(e) => { e.stopPropagation(); setDetail(row); }}
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        title="View my trainings"
                        onClick={(e) => { e.stopPropagation(); navigate("/training"); }}
                    >
                        <GraduationCap className="h-4 w-4" />
                    </Button>
                    {row.status === "completed" && (
                        <Button
                            size="sm"
                            variant="ghost"
                            title="Download certificate"
                            onClick={(e) => {
                                e.stopPropagation();
                                getPrintData(INTERNSHIP_CERTIFICATE, { enrolment_id: row.id });
                            }}
                        >
                            <Award className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={GraduationCap}
                title="My Internship"
                subtitle="Your programme, phase by phase"
                totalRecords={enrolments.length}
            />

            <div className="mb-2 flex flex-wrap items-center gap-3">
                <SegmentSwitch options={STATUS_TABS} value={statusFilter} onChange={setStatusFilter} />
            </div>

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading...</div>
            ) : (
                <Reactable
                    key={`my-internship-${statusFilter}`}
                    pagename="my-internship"
                    data={enrolments}
                    setData={setEnrolments}
                    columns={columns}
                    isCSVRequired
                    isPDFRequired
                    url={MY_INTERNSHIP}
                    printFilter={statusFilter ? { status: statusFilter } : {}}
                    rowSize="sm"
                    tableHeight="calc(100vh - 330px)"
                    onRowClick={(row) => setDetail(row)}
                />
            )}

            <DialogModal
                open={!!detail}
                onClose={() => setDetail(null)}
                title={detail ? detail.program_name : "My internship"}
                showActions={false}
                width="640px"
            >
                {detail && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <EnrolmentStatusPill value={detail.status} />
                            <span className="text-xs text-muted-foreground">
                                {detail.progress_percent ?? 0}% complete
                                {detail.mentor_name ? ` · Mentor: ${detail.mentor_name}` : " · No mentor"}
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {dayjs(detail.start_date).format("DD-MM-YYYY")}
                            {detail.expected_end_date ? ` – ${dayjs(detail.expected_end_date).format("DD-MM-YYYY")}` : ""}
                            {detail.current_phase ? ` · Current: ${detail.current_phase}` : ""}
                        </p>
                        <ol className="space-y-2">
                            {(detail.phase_progress || []).map((p) => (
                                <li key={p.id} className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium">{p.sequence}. {p.phase_name}</span>
                                        <PhaseStatusPill value={p.status} />
                                        {p.rating ? <span className="text-xs text-muted-foreground">rated {p.rating}/5</span> : null}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {p.trainings_total > 0
                                            ? `${p.trainings_completed}/${p.trainings_total} trainings complete`
                                            : "No trainings in this phase"}
                                        {p.evaluated_by ? ` · signed off by ${p.evaluated_by}` : ""}
                                    </p>
                                    {p.remarks ? (
                                        <p className="text-xs italic mt-1 text-muted-foreground">&ldquo;{p.remarks}&rdquo;</p>
                                    ) : null}
                                </li>
                            ))}
                        </ol>
                        <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" onClick={() => navigate("/training")}>
                                View my trainings
                            </Button>
                            {detail.status === "completed" && (
                                <Button
                                    size="sm"
                                    className="bg-sidebar text-primary-foreground hover:bg-sidebar-primary"
                                    onClick={() => getPrintData(INTERNSHIP_CERTIFICATE, { enrolment_id: detail.id })}
                                >
                                    <Award className="h-4 w-4 mr-1" /> Certificate
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </DialogModal>
        </ContainerCard>
    );
};

export default MyInternship;
